import { useState } from "react";

import { Database, Lock } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { useCatalog, useTableDetail } from "../api";
import { SchemaSidebar, type TableRef } from "../components/SchemaSidebar";
import { TableColumnsView } from "../components/TableColumnsView";
import { TableDataView } from "../components/TableDataView";

/**
 * Database Explorer — a two-pane, read-only browser over the cinch_ops Postgres.
 * Left: the schema → table tree. Right: the selected table's columns and a
 * paginated data preview.
 */
export function DatabasePage() {
  const catalog = useCatalog();
  const [selected, setSelected] = useState<TableRef | null>(null);

  return (
    <div className="mx-auto flex h-[calc(100vh-6rem)] max-w-[110rem] flex-col gap-4 pb-4">
      <header className="shrink-0">
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Database className="h-6 w-6 text-power" />
          Database Explorer
        </h1>
        <p className="text-sm text-muted-foreground">
          Browse the cinch_ops application database — schemas, tables, columns and
          a read-only preview of the data. Sensitive columns are masked.
        </p>
      </header>

      <div className="flex min-h-0 flex-1 gap-4">
        <aside className="w-72 shrink-0">
          <SchemaSidebar
            schemas={catalog.data?.schemas ?? []}
            loading={catalog.isLoading}
            selected={selected}
            onSelect={setSelected}
          />
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto">
          {catalog.isError ? (
            <Alert variant="danger">
              <AlertTitle>Couldn’t load the catalog</AlertTitle>
              <AlertDescription>
                {(catalog.error as Error)?.message ?? "Unknown error."}
              </AlertDescription>
            </Alert>
          ) : selected ? (
            <TableInspector key={`${selected.schema}.${selected.table}`} table={selected} />
          ) : (
            <Alert variant="info">
              <Database className="h-4 w-4" />
              <AlertTitle>Select a table</AlertTitle>
              <AlertDescription>
                Pick a table from the tree on the left to see its structure and
                preview its rows.
              </AlertDescription>
            </Alert>
          )}
        </main>
      </div>
    </div>
  );
}

function TableInspector({ table }: { table: TableRef }) {
  const { data, isLoading, isError, error } = useTableDetail(table.schema, table.table);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert variant="danger">
        <AlertTitle>Couldn’t load {table.schema}.{table.table}</AlertTitle>
        <AlertDescription>
          {(error as Error)?.message ?? "Table not found."}
        </AlertDescription>
      </Alert>
    );
  }

  const maskedCount = data.columns.filter((c) => c.sensitive).length;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-mono text-lg font-semibold">
            <span className="text-muted-foreground">{table.schema}.</span>
            {table.table}
          </h2>
          <Badge variant="secondary">{data.kind}</Badge>
          {data.est_rows != null ? (
            <Badge variant="neutral">≈ {data.est_rows.toLocaleString()} rows</Badge>
          ) : null}
          {maskedCount > 0 ? (
            <Badge variant="neutral" className="gap-1">
              <Lock className="h-3 w-3" />
              {maskedCount} masked
            </Badge>
          ) : null}
        </div>
        {data.comment ? (
          <p className="text-sm text-muted-foreground">{data.comment}</p>
        ) : null}
      </div>

      <Tabs defaultValue="columns">
        <TabsList variant="underline">
          <TabsTrigger value="columns" count={data.columns.length}>
            Columns
          </TabsTrigger>
          <TabsTrigger value="data">Data</TabsTrigger>
        </TabsList>
        <TabsContent value="columns" className="pt-2">
          <TableColumnsView detail={data} />
        </TabsContent>
        <TabsContent value="data" className="pt-2">
          <TableDataView schema={table.schema} table={table.table} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
